/**
* wa-template-button.util.ts
*
* What a WhatsApp template button LOOKS LIKE to the end customer, in one place.
*
* Every preview in the product answers the same two questions about a button — which label does the
* customer read, and which of the three affordances does the bubble draw — and each one used to
* answer them on its own: the builder preview, the campaign preview and the attendance preview all
* carried their own copy of the mapping. They must agree, because they are drawing the same message.
*/
import { ButtonItemComponentObject } from '@trendbuild/trend-cloud-api';
import { ButtonActionsTypeEnum } from '@trendbuild/trend-z-api';

/**
 * Labels the provider FIXES. A `COPY_CODE` button travels without `text` — only the offer code is
 * part of the definition — so anything rendering one has to supply the label itself. Kept in English
 * because that is the literal string the provider compares against; never translated.
 */
export const WA_COPY_CODE_FIXED_LABEL = 'Copy offer code';
export const WA_ORDER_DETAILS_FIXED_LABEL = 'Copy Pix code';

const WA_FIXED_BUTTON_LABEL: { [type: string]: string } = {
  COPY_CODE: WA_COPY_CODE_FIXED_LABEL,
  ORDER_DETAILS: WA_ORDER_DETAILS_FIXED_LABEL,
};

/**
 * The three affordances a WhatsApp bubble knows how to draw. Types with no affordance of their own
 * — the copy buttons among them — take the neutral one and are told apart by their label.
 */
const WA_BUTTON_ACTION: { [type: string]: ButtonActionsTypeEnum } = {
  QUICK_REPLY: ButtonActionsTypeEnum.reply,
  URL: ButtonActionsTypeEnum.url,
  PHONE_NUMBER: ButtonActionsTypeEnum.call,
  VOICE_CALL: ButtonActionsTypeEnum.call,
  COPY_CODE: ButtonActionsTypeEnum.reply,
  ORDER_DETAILS: ButtonActionsTypeEnum.reply,
};

/** The label the customer reads — the button's own text, or the label the provider fixes for it. */
export function waTemplateButtonLabel(button: ButtonItemComponentObject): string {
  return button?.text || WA_FIXED_BUTTON_LABEL[button?.type] || '';
}

/** Which affordance the bubble draws for this button type. */
export function waTemplateButtonAction(type: string): ButtonActionsTypeEnum {
  return WA_BUTTON_ACTION[type] ?? ButtonActionsTypeEnum.reply;
}
